import { Button, Tooltip } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../_metronic/_partials/controls";
import { getRegattas } from "../../api/regatta";
import { getBoats } from "../../api/boat";
import { getSailors } from "../../api/sailor";
import { getGpsDevicesStatus } from "../../api/gpsdevice";

const LIVE_URL = "https://live.sailradar.com";
const POLL_MS = 15000;

const STATUS_UI = {
	ok: { color: "#1BC5BD", label: "OK" },
	warn: { color: "#FFA800", label: "Atención" },
	critical: { color: "#F64E60", label: "Problema" },
};

const fmtDate = (d) =>
	d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "";

function fmtAge(ageSec) {
	if (ageSec == null) return "nunca";
	if (ageSec < 90) return `hace ${ageSec} s`;
	if (ageSec < 3600) return `hace ${Math.round(ageSec / 60)} min`;
	if (ageSec < 172800) return `hace ${Math.round(ageSec / 3600)} h`;
	return `hace ${Math.round(ageSec / 86400)} días`;
}

function Dot({ status }) {
	const ui = STATUS_UI[status] || STATUS_UI.critical;
	return (
		<span
			style={{
				width: 10,
				height: 10,
				borderRadius: "50%",
				background: ui.color,
				display: "inline-block",
				marginRight: 8,
				flexShrink: 0,
			}}
		/>
	);
}

/**
 * Dashboard operativo del backoffice: totales, semáforo de la flota GPS en
 * vivo y regatas relevantes con acceso al visor. Antes era una cadena de
 * llamadas anidadas: si una fallaba, la página quedaba en blanco. Ahora
 * cada bloque carga en paralelo y falla por separado.
 */
export function DashboardPage() {
	const history = useHistory();
	const [counts, setCounts] = useState({ regattas: null, boats: null, sailors: null });
	const [fleet, setFleet] = useState(null); // { entries, summary }
	const [regattas, setRegattas] = useState(null);
	const [fleetUpdatedAt, setFleetUpdatedAt] = useState(null);
	const mounted = useRef(true);

	const loadFleet = useCallback(() => {
		getGpsDevicesStatus()
			.then((res) => {
				if (!mounted.current || res.status !== 200) return;
				setFleet(res.data);
				setFleetUpdatedAt(new Date());
			})
			.catch((e) => console.error("dashboard: flota", e));
	}, []);

	useEffect(() => {
		mounted.current = true;

		// cada bloque en paralelo; un fallo no tumba a los demás
		getRegattas()
			.then((res) => {
				if (!mounted.current || res.status !== 200) return;
				const list = Array.isArray(res.data) ? res.data : [];
				setRegattas(list);
				setCounts((c) => ({ ...c, regattas: list.length }));
			})
			.catch((e) => console.error("dashboard: regatas", e));
		getBoats()
			.then((res) => mounted.current && res.status === 200 && setCounts((c) => ({ ...c, boats: res.data.length })))
			.catch((e) => console.error("dashboard: boats", e));
		getSailors()
			.then((res) => mounted.current && res.status === 200 && setCounts((c) => ({ ...c, sailors: res.data.length })))
			.catch((e) => console.error("dashboard: sailors", e));

		loadFleet();
		const iv = setInterval(() => {
			if (document.visibilityState === "visible") loadFleet();
		}, POLL_MS);

		return () => {
			mounted.current = false;
			clearInterval(iv);
		};
	}, [loadFleet]);

	// regatas relevantes: en curso primero, luego próximas, luego recientes
	const now = new Date();
	const sorted = (regattas || []).slice().sort((a, b) => new Date(b.regattaDate) - new Date(a.regattaDate));
	const inProgress = sorted.filter((r) => r.state === "inProgress");
	const upcoming = sorted
		.filter(
			(r) =>
				r.state !== "inProgress" &&
				r.state !== "finalized" &&
				new Date(r.regattaDate) >= new Date(now.toDateString())
		)
		.sort((a, b) => new Date(a.regattaDate) - new Date(b.regattaDate))
		.slice(0, 3);
	const recent = sorted.filter((r) => r.state === "finalized").slice(0, 3);
	const relevant = [...inProgress, ...upcoming, ...recent];

	const problemDevices = (fleet?.entries || []).filter((e) => e.status !== "ok");

	return (
		<>
			{/* Totales */}
			<div className="row">
				{[
					["Regatas", counts.regattas, "/regattas"],
					["Barcos", counts.boats, "/boats"],
					["Regatistas", counts.sailors, "/sailors"],
				].map(([label, value, path]) => (
					<div className="col-12 col-md-4 my-2" key={label}>
						<div className="card" style={{ cursor: "pointer" }} onClick={() => history.push(path)}>
							<div className="card-body text-center">
								<h5 className="card-title mb-1">{label}</h5>
								<div className="contentDash">{value != null ? value : "—"}</div>
							</div>
						</div>
					</div>
				))}
			</div>

			<div className="row mt-2">
				{/* Semáforo de la flota */}
				<div className="col-12 col-lg-6 my-2">
					<Card>
						<CardHeader title="Flota GPS">
							<CardHeaderToolbar>
								{fleet?.summary && (
									<span className="mr-3" style={{ whiteSpace: "nowrap" }}>
										<Dot status="ok" />{fleet.summary.ok}
										<span className="mx-2" />
										<Dot status="warn" />{fleet.summary.warn}
										<span className="mx-2" />
										<Dot status="critical" />{fleet.summary.critical}
									</span>
								)}
								<Tooltip
									title={
										fleetUpdatedAt
											? `Actualizado ${fleetUpdatedAt.toLocaleTimeString()} · auto cada ${POLL_MS / 1000}s`
											: "Refrescar"
									}
								>
									<Button size="small" onClick={loadFleet}>
										<RefreshIcon fontSize="small" />
									</Button>
								</Tooltip>
							</CardHeaderToolbar>
						</CardHeader>
						<CardBody>
							{!fleet && <div className="text-muted">Cargando flota…</div>}
							{fleet && !fleet.entries?.length && (
								<div className="text-muted">No hay dispositivos registrados.</div>
							)}
							{fleet?.entries?.length > 0 && problemDevices.length === 0 && (
								<div style={{ color: STATUS_UI.ok.color, fontWeight: 600 }}>
									✓ Todos los dispositivos operativos
								</div>
							)}
							{problemDevices.map((d) => (
								<div
									key={d._id}
									className="d-flex align-items-center py-2"
									style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
									onClick={() => history.push("/gpsdevices")}
								>
									<Dot status={d.status} />
									<div style={{ minWidth: 0, flex: 1 }}>
										<div style={{ fontWeight: 600 }}>
											{d.reference || "GPS"}{" "}
											<span className="text-muted">· {d.code}</span>
										</div>
										<div className="text-muted" style={{ fontSize: 12 }}>
											{(d.reasons || []).join(" · ")}
										</div>
									</div>
									<div className="text-muted ml-2" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
										{d.batteryLevel != null ? `${d.batteryLevel}% · ` : ""}
										{fmtAge(d.ageSec)}
									</div>
								</div>
							))}
						</CardBody>
					</Card>
				</div>

				{/* Regatas relevantes */}
				<div className="col-12 col-lg-6 my-2">
					<Card>
						<CardHeader title="Regatas" />
						<CardBody>
							{!regattas && <div className="text-muted">Cargando regatas…</div>}
							{regattas && !relevant.length && <div className="text-muted">No hay regatas.</div>}
							{relevant.map((r) => (
								<div
									key={r._id}
									className="d-flex align-items-center py-2"
									style={{ borderBottom: "1px solid #f0f0f0" }}
								>
									<div style={{ minWidth: 0, flex: 1 }}>
										<div style={{ fontWeight: 600 }}>
											{r.fullName}
											{r.state === "inProgress" && (
												<span className="ml-2 badge badge-success">EN CURSO</span>
											)}
											{r.state === "finalized" && (
												<span className="ml-2 badge badge-secondary">Finalizada</span>
											)}
										</div>
										<div className="text-muted" style={{ fontSize: 12 }}>
											{fmtDate(r.regattaDate)}
											{r.startTime ? ` · ${r.startTime}` : ""}
											{r.numParticipants != null ? ` · ${r.numParticipants} barcos` : ""}
										</div>
									</div>
									<button
										type="button"
										className="btn btn-sm btn-light ml-2"
										style={{ whiteSpace: "nowrap" }}
										onClick={() => history.push(`/edit-regatta/${r._id}`)}
									>
										Editar
									</button>
									<a
										href={`${LIVE_URL}/regatta/${r._id}`}
										target="_blank"
										rel="noopener noreferrer"
										className="btn btn-sm btn-light-primary ml-2"
										style={{ whiteSpace: "nowrap" }}
									>
										{r.state === "finalized" ? "Replay" : "Ver en vivo"}
									</a>
								</div>
							))}
						</CardBody>
					</Card>
				</div>
			</div>
		</>
	);
}
